/**
 * LocationAutocomplete Component
 * Google Places autocomplete for address/location input
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { MapPin, Loader2 } from "lucide-react";
import axios from "axios";
import debounce from "lodash/debounce";

const API = process.env.REACT_APP_BACKEND_URL;

export const LocationAutocomplete = ({
  value = "",
  onChange,
  onPlaceSelect,
  placeholder = "Search for a location...",
  disabled = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Update input when value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Debounced fetch function
  const fetchPredictions = useCallback(
    debounce(async (searchInput) => {
      if (!searchInput || searchInput.length < 3) {
        setPredictions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(`${API}/api/places/autocomplete`, {
          input: searchInput,
          region_code: "US",
        });

        setPredictions(response.data.predictions || []);
      } catch (error) {
        console.error("Places autocomplete error:", error);
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    
    if (newValue.length >= 3) {
      setOpen(true);
      fetchPredictions(newValue);
    } else {
      setOpen(false);
      setPredictions([]);
    }
  };

  const handleSelectPlace = async (prediction) => {
    setInputValue(prediction.full_text);
    onChange?.(prediction.full_text);
    setOpen(false);
    setPredictions([]);

    // Fetch full place details
    try {
      const response = await axios.post(`${API}/api/places/details`, {
        place_id: prediction.place_id,
      });

      onPlaceSelect?.({
        place_id: prediction.place_id,
        name: response.data.name,
        address: response.data.formatted_address,
        latitude: response.data.latitude,
        longitude: response.data.longitude,
        full_text: prediction.full_text,
      });
    } catch (error) {
      console.error("Place details error:", error);
      // Still call onPlaceSelect with basic info
      onPlaceSelect?.({
        place_id: prediction.place_id,
        name: prediction.main_text,
        address: prediction.full_text,
        full_text: prediction.full_text,
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={`relative ${className}`}>
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            className="pl-9 pr-8"
            data-testid="location-autocomplete-input"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0" 
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {loading ? (
              <div className="py-6 text-center text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                Searching...
              </div>
            ) : predictions.length === 0 ? (
              <CommandEmpty>No locations found</CommandEmpty>
            ) : (
              <CommandGroup>
                {predictions.map((prediction) => (
                  <CommandItem
                    key={prediction.place_id}
                    onSelect={() => handleSelectPlace(prediction)}
                    className="cursor-pointer px-4 py-3"
                    data-testid={`location-option-${prediction.place_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">
                          {prediction.main_text}
                        </div>
                        <div className="text-sm text-slate-500 truncate">
                          {prediction.secondary_text}
                        </div>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LocationAutocomplete;
